/**
 * Who is signed in, and what they may do.
 *
 * Everyone signs in with a username and a password. The browser keeps an id of
 * its own so its receipts get their own series, but that is bookkeeping, not a
 * check on anybody - there is nothing to register and nothing to approve.
 *
 * `can('stock.receive')` is the only question the app ever asks. It never asks
 * "is this a manager", because roles are rows the owner can invent - the code
 * must not have opinions about their names.
 *
 * Hiding a button is only a courtesy to the user. The server checks the same
 * permission on every call, so a hidden button is not a security measure.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ALL_FEATURES, normalizeFeatures, type FeatureId, type Permission, type User } from '@pos/shared';
import { api, ApiError, getToken, setToken, SESSION_ENDED_EVENT } from '../api.js';
import { deviceId, deviceName, setSeriesPrefix, syncCounter } from '../device.js';
import { rememberForOffline, tryOfflineSignIn, type OfflineResult } from '../offlineLogin.js';
import { db, PosDb } from '../db.js';
import { keyIn, localDbName, normalizeCode, rememberShop, shopKey, thisShop, type ShopRef } from '../shop.js';

export interface DeviceInfo {
  id: string;
  name: string | null;
  seriesPrefix: string | null;
  lastCounter?: number;
  known?: boolean;
}

/** What somebody senior handed over for one action. */
export interface Approval {
  managerId: string;
  managerName: string;
  permissions: Permission[];
  /** Send as the X-Approval header on the call it authorises. */
  approvalToken: string;
}

export interface Subscription {
  status: 'unpaid' | 'active' | 'due_soon' | 'overdue';
  paidUntil: string | null;
  daysLeft: number | null;
}

/** Why an offline sign-in was refused, for the sign-in screen to explain. */
export class OfflineSignInError extends Error {
  constructor(readonly reason: Exclude<OfflineResult, { ok: true }>['reason'] | 'other_shop') {
    super('Offline sign-in refused: ' + reason);
  }
}

/**
 * Signing in to a different shop while this one still has sales waiting to
 * upload. Refused: those sales live in this shop's outbox on this device, and
 * would sit there unsent until somebody switched back.
 */
export class ShopSwitchBlockedError extends Error {
  constructor(readonly waiting: number) {
    super(`${waiting} sale(s) still waiting to upload`);
  }
}

interface AuthCtx {
  user: User | null;
  device: DeviceInfo | null;
  ready: boolean;
  /** True when this session was restored on the device because the server could not be reached. */
  offlineSession: boolean;
  /**
   * `account` is the code typed at sign-in; empty means the default shop.
   * `remember` keeps the session for 30 days instead of 12 hours.
   */
  signInPassword: (username: string, password: string, account?: string, remember?: boolean) => Promise<void>;
  signOut: () => void;
  refreshDevice: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Ask someone senior to authorise exactly these permissions, once. */
  askApproval: (
    username: string,
    password: string,
    permissions: Permission[],
    reason: string,
  ) => Promise<Approval | null>;
  can: (p: Permission) => boolean;
  canAny: (...p: Permission[]) => boolean;
  /** The modules this shop has, as the owner of the system set them. */
  features: FeatureId[];
  hasFeature: (f: FeatureId) => boolean;
  /** The shop's AppBay subscription: dates only. Null when the shop is not billed. */
  subscription: Subscription | null;
}

const Ctx = createContext<AuthCtx | null>(null);

const USER_KEY = 'pos.user';
const DEVICE_CACHE_KEY = 'pos.device.cache';
const FEATURES_KEY = 'pos.features';

/** What the device last heard. Until it has heard anything: every module - the server still decides. */
function cachedFeatures(): FeatureId[] {
  try {
    const raw = localStorage.getItem(shopKey(FEATURES_KEY));
    return raw ? normalizeFeatures(JSON.parse(raw)) : [...ALL_FEATURES];
  } catch {
    return [...ALL_FEATURES];
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const raw = localStorage.getItem(shopKey(USER_KEY));
    return raw && getToken() ? (JSON.parse(raw) as User) : null;
  });
  const [device, setDevice] = useState<DeviceInfo | null>(null);
  const [ready, setReady] = useState(false);
  const [offlineSession, setOfflineSession] = useState(false);
  const [features, setFeaturesState] = useState<FeatureId[]>(cachedFeatures);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const setFeatures = useCallback((list: unknown, storageKey?: string) => {
    if (!Array.isArray(list)) return;
    const f = normalizeFeatures(list);
    localStorage.setItem(storageKey === undefined ? shopKey(FEATURES_KEY) : keyIn(storageKey, FEATURES_KEY), JSON.stringify(f));
    if (storageKey === undefined) setFeaturesState(f);
  }, []);

  const signOut = useCallback(() => {
    void api('/auth/logout', { body: {} }).catch(() => undefined);
    setToken(null);
    localStorage.removeItem(shopKey(USER_KEY));
    localStorage.removeItem(shopKey('pos.shift'));
    setUser(null);
    setOfflineSession(false);
  }, []);

  // The server said the session is over. Clear the screen too - a till that
  // still LOOKS signed in but fails every call is the worst of both worlds.
  // The outbox is untouched: queued sales upload after the next sign-in.
  useEffect(() => {
    const ended = () => {
      localStorage.removeItem(shopKey(USER_KEY));
      setUser(null);
      setOfflineSession(false);
      sessionStorage.setItem('pos.sessionEnded', '1');
    };
    window.addEventListener(SESSION_ENDED_EVENT, ended);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, ended);
  }, []);

  const refreshDevice = useCallback(async () => {
    try {
      const d = await api<DeviceInfo>(`/auth/device/${deviceId()}`, { auth: false });
      setDevice(d);
      if (d.seriesPrefix) setSeriesPrefix(d.seriesPrefix);
      if (d.lastCounter) syncCounter(d.lastCounter);
      localStorage.setItem(shopKey(DEVICE_CACHE_KEY), JSON.stringify(d));
    } catch (e) {
      // Offline: keep whatever this device last knew about itself.
      if (e instanceof ApiError && e.isOffline) {
        const cached = localStorage.getItem(shopKey(DEVICE_CACHE_KEY));
        if (cached) setDevice(JSON.parse(cached) as DeviceInfo);
      } else {
        setDevice(null);
      }
    }
  }, []);

  /** Pick up a permission the owner changed while this person was signed in. */
  const refreshUser = useCallback(async () => {
    try {
      const res = await api<{ user: User; features?: string[]; subscription?: Subscription | null }>('/auth/me');
      setSubscription(res.subscription ?? null);
      setUser(res.user);
      localStorage.setItem(shopKey(USER_KEY), JSON.stringify(res.user));
      setFeatures(res.features);
    } catch {
      /* offline - the cached permissions stay in force */
    }
  }, [setFeatures]);

  useEffect(() => {
    void refreshDevice().finally(() => setReady(true));
  }, [refreshDevice]);

  useEffect(() => {
    if (user) void refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = useCallback((res: { token: string; user: User; device?: DeviceInfo | null }) => {
    setToken(res.token);
    localStorage.setItem(shopKey(USER_KEY), JSON.stringify(res.user));
    setUser(res.user);
    // The server creates the till record on first sign-in and hands back its
    // receipt prefix, so numbering is right even if the connection drops now.
    if (res.device) {
      setDevice(res.device);
      if (res.device.seriesPrefix) setSeriesPrefix(res.device.seriesPrefix);
      if (res.device.lastCounter) syncCounter(res.device.lastCounter);
      localStorage.setItem(shopKey(DEVICE_CACHE_KEY), JSON.stringify(res.device));
    }
  }, []);

  const signInPassword = useCallback(
    async (username: string, password: string, account = '', remember = false) => {
      const code = normalizeCode(account);
      let res: {
        token: string;
        user: User;
        device?: DeviceInfo | null;
        account?: ShopRef;
        features?: string[];
        subscription?: Subscription | null;
      };
      try {
        res = await api('/auth/login', {
          body: { username, password, deviceId: deviceId(), deviceName: deviceName() || undefined, remember },
          auth: false,
          account: code || null,
        });
      } catch (e) {
        // Only NO CONNECTION falls back to the device. A refusal is final.
        if (!(e instanceof ApiError && e.isOffline)) throw e;
        // The device only holds the shop it belongs to. A blank code means that
        // shop only on a till from before account codes, which was never told one.
        const here = thisShop();
        if (code ? code !== here?.code : !!here?.storageKey) throw new OfflineSignInError('other_shop');
        const local = await tryOfflineSignIn(username, password);
        if (!local.ok) throw new OfflineSignInError(local.reason);
        finish({ ...local.session, device: local.session.device as DeviceInfo | null });
        setOfflineSession(true);
        return;
      }
      const shop = res.account;
      const session = { token: res.token, user: res.user, device: res.device ?? null };
      if (shop && shop.storageKey !== (thisShop()?.storageKey ?? '')) {
        setFeatures(res.features, shop.storageKey);
        await switchShop(shop, session, username, password);
        return;
      }
      if (shop) rememberShop(shop);
      setFeatures(res.features);
      setSubscription(res.subscription ?? null);
      finish(res);
      setOfflineSession(false);
      // So this person can get back in next time the internet is down.
      await rememberForOffline(username, password, session).catch(() => undefined);
    },
    [finish, setFeatures],
  );

  const askApproval = useCallback(
    async (
      username: string,
      password: string,
      permissions: Permission[],
      reason: string,
    ): Promise<Approval | null> => {
      try {
        const res = await api<Approval & { approved: boolean }>('/auth/approve', {
          body: { username, password, permissions, reason },
        });
        return res.approved ? res : null;
      } catch {
        return null;
      }
    },
    [],
  );

  const can = useCallback((p: Permission) => !!user?.permissions?.includes(p), [user]);
  const canAny = useCallback(
    (...p: Permission[]) => p.some((x) => !!user?.permissions?.includes(x)),
    [user],
  );

  const hasFeature = useCallback((f: FeatureId) => features.includes(f), [features]);

  const value = useMemo<AuthCtx>(
    () => ({
      user, device, ready, offlineSession,
      signInPassword, signOut,
      refreshDevice, refreshUser,
      askApproval, can, canAny,
      features, hasFeature, subscription,
    }),
    [
      user, device, ready, offlineSession, signInPassword, signOut, refreshDevice, refreshUser, askApproval, can, canAny,
      features, hasFeature, subscription,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth used outside the provider');
  return c;
}

/**
 * Signed in to a different shop from the one this page was loaded for. The
 * session is filed under the new shop, then the page reloads so every screen,
 * timer and cached list starts again from that shop's own data.
 */
async function switchShop(
  shop: ShopRef,
  session: { token: string; user: User; device: DeviceInfo | null },
  username: string,
  password: string,
): Promise<void> {
  // Every sale in the outbox counts, stuck ones too: a stuck sale needs
  // somebody in THIS shop to look at it.
  const waiting = await db.outbox.count();
  if (waiting > 0) throw new ShopSwitchBlockedError(waiting);

  const key = shop.storageKey;
  setToken(session.token, key);
  localStorage.setItem(keyIn(key, USER_KEY), JSON.stringify(session.user));
  if (session.device) {
    if (session.device.seriesPrefix) setSeriesPrefix(session.device.seriesPrefix, key);
    if (session.device.lastCounter) syncCounter(session.device.lastCounter, key);
    localStorage.setItem(keyIn(key, DEVICE_CACHE_KEY), JSON.stringify(session.device));
  }
  const target = new PosDb(localDbName(key));
  await rememberForOffline(username, password, session, target).catch(() => undefined);
  target.close();
  rememberShop(shop);
  window.location.reload();
}

/** Render children only when the person holds the permission. */
export function Allowed({ need, children }: { need: Permission; children: ReactNode }) {
  const { can } = useAuth();
  return can(need) ? <>{children}</> : null;
}
